/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import HeaderSection from '../../common/headerSection';
import PageHeader from '../../common/pageHeader';
import { useTranslation } from 'react-i18next';
import SterilityTestingInitiation from './SterilityTestingInitiation';
import SterilityTestDetails from './SterilityTestDetails';
import Pagination from '../../../widgets/pagination';
import { DEFAULT_PAGE_SIZE } from '../../../constants/constants';
import SterilityObservationRecording from './SterilityObservationRecording';
import SterilityObservationDetails from './SterilityObservationDetails';
import { SterilityTestServices } from '../../../service/sterilityTestServices';
import { helperFunctions } from '../../../utils/helper';
import CustomToast from '../../../widgets/toaster';
// import SterilityTestingInitiation from './SterilityTestingInitiation';

interface SterilityTest {
    id: string;
    testIdentifier: string;
    productName: string;
    batchNumber: string;
    method: 'Membrane Filtration' | 'Direct Inoculation';
    mediaUsed: string[];
    currentStage: string;
    progress: number;
    nextObservation: string;
    observationStatus: 'Upcoming' | 'Due Today' | 'Completed' | 'Overdue';
    startDate: string;
    expectedCompletion: string;
    analyst: {
        name: string;
        avatar: string;
    };
    qaStatus: 'Pending' | 'Approved' | 'Rejected';
    overallStatus: 'Active' | 'Incubating' | 'Completed' | 'Failed' | 'Cancelled';
}

const MOCK_TESTS: SterilityTest[] = [
    {
        id: '1',
        testIdentifier: 'ST-2024-0812',
        productName: 'Ampicillin Injection 500mg',
        batchNumber: 'LOT-2024-X45',
        method: 'Membrane Filtration',
        mediaUsed: ['FTM', 'SCDM'],
        currentStage: 'Day 7 of 14',
        progress: 50,
        nextObservation: '2024-08-19 14:00',
        observationStatus: 'Due Today',
        startDate: '2024-08-12',
        expectedCompletion: '2024-08-26',
        analyst: { name: 'Sarah Connor', avatar: 'SC' },
        qaStatus: 'Pending',
        overallStatus: 'Incubating'
    },
    {
        id: '2',
        testIdentifier: 'ST-2024-0815',
        productName: 'Tryptic Soy Broth 100mL',
        batchNumber: 'LOT-2024-X89',
        method: 'Direct Inoculation',
        mediaUsed: ['FTM'],
        currentStage: 'Day 3 of 14',
        progress: 21,
        nextObservation: '2024-08-18 09:30',
        observationStatus: 'Upcoming',
        startDate: '2024-08-15',
        expectedCompletion: '2024-08-29',
        analyst: { name: 'James Wilson', avatar: 'JW' },
        qaStatus: 'Pending',
        overallStatus: 'Active'
    },
    {
        id: '3',
        testIdentifier: 'ST-2024-0805',
        productName: 'Insulin Glargine 100 IU/mL',
        batchNumber: 'LOT-2024-Z22',
        method: 'Membrane Filtration',
        mediaUsed: ['FTM', 'SCDM'],
        currentStage: 'Day 10 of 14',
        progress: 71,
        nextObservation: '2024-08-15 11:00',
        observationStatus: 'Overdue',
        startDate: '2024-08-05',
        expectedCompletion: '2024-08-19',
        analyst: { name: 'Elena Gilbert', avatar: 'EG' },
        qaStatus: 'Pending',
        overallStatus: 'Incubating'
    },
    {
        id: '4',
        testIdentifier: 'ST-2024-0801',
        productName: 'Sodium Chloride 0.9% 500mL',
        batchNumber: 'LOT-2024-W11',
        method: 'Direct Inoculation',
        mediaUsed: ['SCDM'],
        currentStage: 'Day 14 of 14',
        progress: 100,
        nextObservation: '2024-08-15 08:00',
        observationStatus: 'Completed',
        startDate: '2024-08-01',
        expectedCompletion: '2024-08-15',
        analyst: { name: 'Marcus Aurelius', avatar: 'MA' },
        qaStatus: 'Approved',
        overallStatus: 'Completed'
    },
    {
        id: '5',
        testIdentifier: 'ST-2024-0728',
        productName: 'Ciprofloxacin 200mg/100mL',
        batchNumber: 'LOT-2024-C99',
        method: 'Membrane Filtration',
        mediaUsed: ['FTM', 'SCDM'],
        currentStage: 'Day 5 of 14',
        progress: 35,
        nextObservation: '2024-08-02 15:30',
        observationStatus: 'Overdue',
        startDate: '2024-07-28',
        expectedCompletion: '2024-08-11',
        analyst: { name: 'Sarah Connor', avatar: 'SC' },
        qaStatus: 'Rejected',
        overallStatus: 'Failed'
    }
];

interface SterilityTestingProps {
    onViewDetails?: (id: string) => void;
    onRecordObservation?: (id: string) => void;
}

const SterilityTesting: React.FC<SterilityTestingProps> = ({ onViewDetails, onRecordObservation }) => {

    const {t} = useTranslation();
    const [isInitiating, setIsInitiating] = useState(false);
    const [showSterilityDetails, setShowSterilityDetails] = useState(false);
    const [showRecordObservation, setShowRecordObservation] = useState(false);
    const [showViewObservation, setShowViewObservation] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [activeFilter, setActiveFilter] = useState<'All' | 'Active' | 'Incubating' | 'Due Today' | 'Overdue' | 'Completed' | 'Failed'>('All');
    const [density, setDensity] = useState<'Comfortable' | 'Compact'>('Comfortable');
    const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

    // pagination
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [sort, setSort] = useState("id");
    const [order, setOrder] = useState("desc");
    const [userId, setUserId] = useState<number>(0);
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState("");
    const [toastType, setToastType] = useState("");
    const [useEffectRequired, setUseEffectRequired] = useState(false);
    const [testType, settestType] = useState(0);
    const [sterilityTestStatus, setSterilityTestStatus] = useState("");
     const [sterilityTestId, setSterilityTestId] = useState("");
    const [fromdate, setFromDate] = useState(
        new Date(new Date().setDate(new Date().getDate() - 7)).toISOString().split('T')[0]
    );
    const currentDate = new Date().toISOString().split('T')[0];
    const [toDate, setToDate] = useState(currentDate);
    const [userName, setUserName] = useState("")    
    

    const [sterilityTestList, setSterilityTestList] = useState([]);

    const toggleRow = (id: string) => {
        const newExpanded = new Set(expandedRows);
        if (newExpanded.has(id)) newExpanded.delete(id);
        else newExpanded.add(id);
        setExpandedRows(newExpanded);
    };

    const filteredTests = useMemo(() => {
        return MOCK_TESTS.filter(test => {
            const matchesSearch =
                test.testIdentifier.toLowerCase().includes(searchQuery.toLowerCase()) ||
                test.productName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                test.batchNumber.toLowerCase().includes(searchQuery.toLowerCase());

            const matchesFilter =
                activeFilter === 'All' ||
                (activeFilter === 'Active' && test.overallStatus === 'Active') ||
                (activeFilter === 'Incubating' && test.overallStatus === 'Incubating') ||
                (activeFilter === 'Due Today' && test.observationStatus === 'Due Today') ||
                (activeFilter === 'Overdue' && test.observationStatus === 'Overdue') ||
                (activeFilter === 'Completed' && test.overallStatus === 'Completed') ||
                (activeFilter === 'Failed' && test.overallStatus === 'Failed');

            return matchesSearch && matchesFilter;
        });
    }, [searchQuery, activeFilter]);

    const [kpis, setKpis] = useState([
        { label: 'Active Tests', value: '0', icon: 'science', color: 'blue', key: 'active_tests' },
        { label: 'Incubating', value: '0', icon: 'thermostat', color: 'purple', key: 'incubating' },
        { label: 'Due Today', value: '0', icon: 'event_upcoming', color: 'orange', key: 'due_today' },
        { label: 'Overdue', value: '0', icon: 'warning', color: 'red', key: 'overdue' },
        { label: 'Monthly Done', value: '0', icon: 'task_alt', color: 'emerald', key: 'monthly_done' },
    ]);

    const getStatusColor = (status: SterilityTest['overallStatus']) => {
        switch (status) {
            case 'Active': return 'bg-blue-50 text-blue-600 border-blue-100';
            case 'Incubating': return 'bg-purple-50 text-purple-600 border-purple-100';
            case 'Completed': return 'bg-emerald-50 text-emerald-600 border-emerald-100';
            case 'Failed': return 'bg-red-50 text-red-600 border-red-100';
            case 'Cancelled': return 'bg-slate-50 text-slate-600 border-slate-100';
            default: return 'bg-slate-50 text-slate-600';
        }
    };

    const getObsStatusColor = (status: SterilityTest['observationStatus']) => {
        switch (status) {
            case 'Upcoming': return 'bg-slate-100 text-slate-600 border-slate-200';
            case 'Due Today': return 'bg-orange-50 text-orange-600 border-orange-100';
            case 'Completed': return 'bg-emerald-50 text-emerald-600 border-emerald-100';
            case 'Overdue': return 'bg-red-50 text-red-600 border-red-100';
            default: return 'bg-slate-100 text-slate-600';
        }
    };

    const getQAStatusColor = (status: SterilityTest['qaStatus']) => {
        switch (status) {
            case 'Pending': return 'bg-amber-50 text-amber-600 border-amber-100';
            case 'Approved': return 'bg-emerald-50 text-emerald-600 border-emerald-100';
            case 'Rejected': return 'bg-red-50 text-red-600 border-red-100';
            default: return 'bg-slate-50 text-slate-600';
        }
    };

    const getInitials = (name?: string | null) => {
        if (!name) return '--';
        return name
            .split(' ')
            .filter(Boolean)
            .map(word => word[0]?.toUpperCase())
            .slice(0, 2)
            .join('');
    };

    //api call for table sterility test listing 
    const getSterilityTestList = async () => {
        try {
            const response = await SterilityTestServices.getSterilityTestList(page, pageSize, sort, order, testType, sterilityTestStatus, fromdate, toDate)

            if (response.status && response.statusCode === 200) {
                try {
                    if (Array.isArray(response.data) && response.data?.length > 0) {
                        setTotalPages(response?.totalPages)
                        setTotalElements(response?.totalElements)
                        let rowData: any = response.data.map((element: any, index: number) => {

                            return {
                                id: element.id,
                                testIdentifier: element.sterility_test_identifier,
                                productName: element.product_name ?? '—',
                                batchNumber: element.batch_no,
                                method: element.test_method_name,
                                methodId: element.test_method_id,

                                // media/plate info
                                plateType: element.plate_type,
                                tubeSize: element.tube_size,
                                tubeSizeUom: element.tube_size_uom,
                                tubeCount: element.tube_count,

                                // schedule / status
                                productionScheduleName: element.production_schedule_name ?? '—',
                                overallStatus: element.sterility_test_status,

                                // dates
                                startDate: helperFunctions.formatDate(element.sterility_test_start_date_time),
                                completedAt: helperFunctions.formatDate(element.sterility_test_completed_at),
                                createdAt: helperFunctions.formatDate(element.created_at),
                                updatedAt: helperFunctions.formatDate(element.updated_at),

                                // audit
                                createdBy: {
                                    id: element.created_by_id ?? element.created_by,
                                    name: element.created_by_name,
                                },
                                updatedBy: element.updated_by,

                                // fields not (yet) provided by the API — placeholder/derived
                                currentStage: element.current_stage ?? '-',
                                progress: element.progress ?? 0,
                                nextObservation: element.next_observation ?? '-',
                                observationStatus: element.observation_status ?? 'Upcoming',
                                expectedCompletion: element.expected_completion ?? '-',
                                analyst: {
                                    name: element.created_by_name ?? 'Unassigned',
                                    avatar: getInitials(element.created_by_name),
                                },
                                qaStatus: element.qa_status ?? 'Pending',

                            };
                        });
                        setSterilityTestList(rowData);
                    }
                } catch (error) {
                    console.log(error);
                }
            } else {
                setSterilityTestList([])
                setTotalPages(0)
                setTotalElements(0)
            }
        } catch (error) {
            console.error('Plate response', error);
        }

    }

    //api call sterility test stats 
    const getSterilityTestStat = async () => {
        try {
            const response = await SterilityTestServices.getSterilityTestStats()

            if (response.status && response.statusCode === 200) {
                const stats = response.data || {};

                setKpis(prev =>
                    prev.map(kpi => ({
                        ...kpi,
                        value: String(stats[kpi.key] ?? 0),
                    }))
                );
            } else {
                console.warn('Unexpected response fetching sterility test stats', response);
            }
        } catch (error) {
            console.error('Plate response', error);
        }

    }

    //handle sterility test submit click
    const handleSterilityTestStartClick = async (data: any) => {

        let dataToSend = {};

        let testType = data.testMethod === "Direct" ? 1 : 2 
        let tubeIds = data.tubes.map((item: any) => item.id).join(',');

        if (testType === 1) {
            dataToSend = {
                user_id: userId,
                test_type: testType,
                tube_ids: tubeIds
            }
        } else {
            dataToSend = {
                user_id: userId,
                test_type: testType,
                tube_ids: tubeIds,
                filtration_done: data.membraneDatafiltrationDone === "Yes" ? 1 : 0,
                membrane_id: data.membraneData.filterId,
                wash_cycles: Number(data.membraneData.washCycles),
                remarks:data.membraneData.remarks
            }
        }

        try {
            const response = await SterilityTestServices.sterilityTestStart(dataToSend);
            if (response.status && response.statusCode === 200) {
                setToastType("success")
                setToastMessage(response.message)
            } else {
                setToastType("error")
                setToastMessage(response.message)
            }
            setIsInitiating(false)
            setShowToast(true)
            // addEquipmentModalClose()
            setUseEffectRequired(true)
        } catch (error) {
            console.error('user response', error);
        }
    };

    //handle table pagination 
    const handlePageChange = (newPage: any, pageSize?: number) => {
        setPage(newPage);
        if (pageSize) {
        setPageSize(pageSize)
        }
        setUseEffectRequired(true)
    };

    useEffect(() => {
        getSterilityTestList();
        getSterilityTestStat();
        setUseEffectRequired(false);
    }, [useEffectRequired, fromdate, toDate, sterilityTestStatus, testType])

    useEffect(() => {
        var storedData = localStorage.getItem('userDetails');

        if (storedData) {
            var storedObject = JSON.parse(storedData);
            setUserId(storedObject[0].id);
            setUserName(storedObject[0]?.first_name + " " + storedObject[0]?.last_name);
        }
    }, [])


    return (
        <div>
            <HeaderSection />
            <PageHeader pageHeader={t('sterilityTesting.title')} pageDescription={t('pageDescription.sterilityTesting.description')} />
            <CustomToast
                show={showToast}
                onClose={() => setShowToast(false)}
                delay={5000}
                message={toastMessage}
                toastType={toastType}
            />

            {isInitiating && (
                <SterilityTestingInitiation 
                    onCancel={() => setIsInitiating(false)}
                    onSubmit={(data: any) => handleSterilityTestStartClick(data)}
                    userName={userName}
                />
            ) }

            {showSterilityDetails && (
                <SterilityTestDetails
                    sterilityTestId={sterilityTestId}
                    onBack={() => setShowSterilityDetails(false)}
                    onRecord={() => { setShowRecordObservation(true); setShowSterilityDetails(false) }}
                    onViewObservation={() => { setShowViewObservation(true); setShowSterilityDetails(false) }}
                />
            )}

            {showRecordObservation && (
                <SterilityObservationRecording
                onBack={() => setShowRecordObservation(false)}
                onSave={() => alert("on save")}
            />
            )}

            
            {showViewObservation && (
                <SterilityObservationDetails
                    onBack={() => { setShowViewObservation(false); setShowSterilityDetails(true) }}
                    onViewTest={() => { setShowViewObservation(false); setShowSterilityDetails(true) }}
                />
            )}

            {!isInitiating && !showSterilityDetails && !showRecordObservation && !showViewObservation && (

            <div className="min-h-screen bg-[#F8FAFC] p-8 pb-20 media-prep-scope">
                {/* Header */}
                <div className="flex flex-col xl:flex-row xl:items-center justify-end gap-6 my-2">
                    {/* <div>
                        <h1 className="text-2xl font-black text-[#1E293B] tracking-tight">Sterility Testing</h1>
                        <p className="text-slate-500 text-sm font-medium mt-1">Initiate and monitor sterility testing cycles throughout their lifecycle.</p>
                    </div> */}
                    <div className="flex flex-wrap items-center gap-3">
                        {/* <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-xl border border-slate-200 shadow-sm min-w-[140px]">
                            <span className="material-symbols-outlined text-slate-400 text-lg">calendar_today</span>
                            <select className="text-[11px] font-bold text-slate-700 uppercase tracking-wider outline-none bg-transparent w-full">
                                <option>All Time</option>
                                <option>Today</option>
                                <option>This Week</option>
                                <option>This Month</option>
                            </select>
                        </div> */}
                        {/* <div className="relative">
                            <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-lg">search</span>
                            <input
                                type="text"
                                placeholder="Search by ID, Product..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-700 focus:ring-2 focus:ring-blue-500/20 outline-none w-64 transition-all"
                            />
                        </div>
                        <button className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-xl text-[11px] font-bold uppercase tracking-wider hover:bg-slate-50 transition-all shadow-sm">
                            <span className="material-symbols-outlined text-lg">filter_list</span>
                            Filters
                        </button>
                        <button className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-xl text-[11px] font-bold uppercase tracking-wider hover:bg-slate-50 transition-all shadow-sm">
                            <span className="material-symbols-outlined text-lg">ios_share</span>
                            Export
                        </button> */}
                        <button
                            onClick={() => setIsInitiating(true)}
                            className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl text-[11px] font-black uppercase tracking-wider hover:bg-blue-700 transition-all"
                        >
                            <span className="material-symbols-outlined text-lg">add</span>
                            New Sterility Test
                        </button>
                    </div>
                </div>

                {/* KPI Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 my-2">
                    {kpis.map((kpi, idx) => (
                        <motion.div
                            key={idx}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: idx * 0.05 }}
                            className="bg-white p-2 rounded-2xl border border-slate-200 shadow-sm group hover:border-blue-200 transition-all"
                        >
                            <div className="flex items-center justify-between mb-1">
                                <div className={`w-8 h-8 rounded-lg bg-${kpi.color}-50 flex items-center justify-center text-${kpi.color}-600`}>
                                    <span className="material-symbols-outlined text-lg font-black">{kpi.icon}</span>
                                </div>
                                <span className={`text-[10px] font-black text-${kpi.color}-600 bg-${kpi.color}-50 px-2 py-0.5 rounded-full uppercase`}>
                                    Live
                                </span>
                            </div>
                            <p className="text-[10px] mb-0 font-black text-slate-400 uppercase tracking-widest">{kpi.label}</p>
                            <p className="text-2xl font-black mb-0 text-[#1E293B] mt-0.5">{kpi.value}</p>
                        </motion.div>
                    ))}
                </div>


                {/* Filters Ribbon */}
                <div className='my-3' >
                    <div className="d-flex justify-content-end gap-3">
                    <div className="space-y-1.5">
                        <label className="filterLabelLeft">Test Method</label>
                        <select 
                        value={testType}
                        onChange={(e) => settestType(Number(e.target.value))}
                        className="areaTypeSelect"
                        >
                        <option value={0} >All Methods</option>
                        <option value={1} >Direct Inoculation</option>
                        <option value={2} >Membrane Filtration</option>
                        </select>
                    </div>
                    <div className="space-y-1.5">
                        <label className="filterLabelLeft">Status</label>
                        <select 
                        value={sterilityTestStatus}
                        onChange={(e) => setSterilityTestStatus(e.target.value)}
                        className="areaTypeSelect"
                        >
                        <option value="">All Statuses</option>
                        <option value="2">Sterility Testing Assigned</option>
                        <option value="3">Media Tube Incubation Inrogress</option>
                        <option value="4">Media Tube Incubation Completed</option>
                        </select>
                    </div>
                    <div className="space-y-1.5">
                        <label className="filterLabelLeft">From Date</label>
                        <input 
                            type="date" 
                            value={fromdate}
                            onChange={(e) => setFromDate(e.target.value)}
                            className="form-control mt-2"
                        />
                    </div>
                    <div className="space-y-1.5">
                        <label className="filterLabelLeft">To Date</label>
                        <input 
                            type="date" 
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                            className="form-control mt-2"
                        />
                    </div>
                    </div>
                </div>

                {/* Main Table */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-scroll my-4 h-[60vh]">
                        <table className="w-full min-w-[1400px] text-center border-collapse [&_th]:align-middle [&_td]:align-middle">
                            <thead className="sticky top-0 z-20 bg-white border-b border-slate-100">
                                <tr className="tableHeadRow text-center">
                                    {/* <th className="tableHeadData2 px-4 py-3">
                                        <input type="checkbox" className="rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
                                    </th> */}
                                    <th className="tableHeadData2">Test ID</th>
                                    <th className="tableHeadData2">Product</th>
                                    <th className="tableHeadData2">Batch</th>
                                    <th className="tableHeadData2">Method</th>
                                    <th className="tableHeadData2">Media</th>
                                    <th className="tableHeadData2">Current Stage</th>
                                    <th className="tableHeadData2">Next Observation</th>
                                    <th className="tableHeadData2">Analyst</th>
                                    <th className="tableHeadData2">Status</th>
                                    <th className="tableHeadData2">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {sterilityTestList.length > 0 ? (
                                    sterilityTestList.map((test : any) => (
                                        <React.Fragment key={test.testIdentifier}>
                                            <tr
                                                onClick={() => toggleRow(test.testIdentifier)}
                                                className={`hover:bg-blue-50/30 transition-all cursor-pointer group ${expandedRows.has(test.testIdentifier) ? 'bg-blue-50/20' : ''}`}
                                            >
                                                {/* <td className="px-2 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                                                    <input type="checkbox" className="rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
                                                </td> */}
                                                <td className="px-2 py-3 text-center align-middle">
                                                    <span className="text-[11px] whitespace-nowrap font-black text-blue-600 hover:underline">{test.testIdentifier}</span>
                                                </td>
                                                <td className="px-2 py-3 text-center align-middle">
                                                    <div>
                                                        <p className="m-0 text-[11px] font-black text-slate-700">{test.productName}</p>
                                                    </div>
                                                </td>
                                                <td className="px-2 py-3 text-center align-middle">
                                                    <div>
                                                        <p className="m-0 text-[11px] whitespace-nowrap font-black text-slate-700">{test.batchNumber}</p>
                                                    </div>
                                                </td>
                                                <td className="px-2 py-3 text-center align-middle">
                                                    <div className="space-y-1">
                                                        <p className="m-0 text-[11px] font-bold text-slate-600">{test.method}</p>
                                                    </div>
                                                </td>
                                                {/* <td className="px-2 py-3">
                                                        <div className="flex gap-1">
                                                            {test.mediaUsed?.map((m : any) => (
                                                                <span key={m} className="px-1.5 py-0.5 rounded-md bg-blue-50 text-blue-600 text-[8px] font-black border border-blue-100">{m}</span>
                                                            ))}
                                                        </div>
                                                </td> */}
                                                <td className="px-2 py-3 text-center align-middle">
                                                    {test.mediaUsed?.length > 0 ? (
                                                        <div className="flex h-full items-center justify-center gap-1">
                                                        {test.mediaUsed.map((m: any) => (
                                                            <span
                                                            key={m}
                                                            className="px-1.5 py-0.5 rounded-md bg-blue-50 text-blue-600 text-[8px] font-black border border-blue-100"
                                                            >
                                                            {m}
                                                            </span>
                                                        ))}
                                                        </div>
                                                    ) : (
                                                        <span>-</span>
                                                    )}
                                                </td>
                                                <td className="px-2 py-3 text-center align-middle">
                                                    <div className="flex h-full items-center justify-center">
                                                        <span className="text-[11px] font-black text-slate-700">{test.currentStage}</span>
                                                    </div>
                                                </td>
                                                {/* <td className="px-2 py-3">
                                                    <div>
                                                        <p className="text-[10px] font-bold text-slate-700">{test.nextObservation}</p>
                                                        <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-wider border ${getObsStatusColor(test.observationStatus)}`}>
                                                            {test.observationStatus}
                                                        </span>
                                                    </div>
                                                </td> */}
                                                <td className="px-2 py-3 text-center align-middle">
                                                    <div className="flex flex-col items-center justify-center">
                                                        <p className="m-0 text-[10px] font-bold text-slate-700">
                                                        {test.nextObservation}
                                                        </p>
                                                        <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-wider border ${getObsStatusColor(test.observationStatus)}`}>
                                                        {test.observationStatus}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-2 py-3">
                                                    <div className="flex items-center gap-2">
                                                        <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-[9px] font-black text-blue-600 border border-blue-200">
                                                            {test.analyst.avatar}
                                                        </div>
                                                        <span className="text-[10px] whitespace-nowrap font-bold text-slate-600">{test.analyst.name}</span>
                                                    </div>
                                                </td>
                                                <td className="px-2 py-3">
                                                    <div className="flex items-center gap-2">
                                                        <span className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-wider border ${getStatusColor(test.overallStatus)}`}>
                                                            {test.overallStatus}
                                                        </span>
                                                        {test.qaStatus !== 'Pending' && (
                                                            <span className={`px-2 py-0.5 rounded-md text-[8px] font-black uppercase tracking-wider border ${getQAStatusColor(test.qaStatus)}`}>
                                                                QA: {test.qaStatus}
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-2 py-3">
                                                    <div className="flex items-center">
                                                        <button
                                                            title="View Details"
                                                            onClick={(e) => { setShowSterilityDetails(true); setSterilityTestId(test.id) }}
                                                            className="p-1.5 rounded-lg text-slate-400 hover:bg-blue-50 hover:text-blue-600 transition-all"
                                                        >
                                                            <span className="material-symbols-outlined text-lg">visibility</span>
                                                        </button>
                                                        {/* <button
                                                            title="Record Observation"
                                                            onClick={(e) => { e.stopPropagation(); onRecordObservation?.(test.testIdentifier); setShowRecordObservation(true); }}
                                                            className="p-1.5 rounded-lg text-slate-400 hover:bg-blue-50 hover:text-blue-600 transition-all"
                                                        >
                                                            <span className="material-symbols-outlined text-lg">edit_note</span>
                                                        </button> */}
                                                        {/* <button
                                                            title="More"
                                                            className="p-1.5 rounded-lg text-slate-400 hover:bg-blue-50 hover:text-blue-600 transition-all"
                                                        >
                                                            <span className="material-symbols-outlined text-lg">more_vert</span>
                                                        </button> */}
                                                    </div>
                                                </td>
                                            </tr>

                                            {/* Expanded Detail Row */}
                                            {/* <AnimatePresence>
                                                {expandedRows.has(test.testIdentifier) && (
                                                    <motion.tr
                                                        initial={{ opacity: 0, height: 0 }}
                                                        animate={{ opacity: 1, height: 'auto' }}
                                                        exit={{ opacity: 0, height: 0 }}
                                                        className="bg-slate-50/50"
                                                    >
                                                        <td colSpan={10} className="px-10 py-6">
                                                            <div className="grid grid-cols-4 gap-8">
                                                                <div className="space-y-4">
                                                                    <div>
                                                                        <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">Observation Timeline</h4>
                                                                        <div className="space-y-3 relative before:absolute before:left-[11px] before:top-2 before:bottom-2 before:w-px before:bg-slate-200">
                                                                            {[
                                                                                { day: 'Day 1', status: 'Completed', date: '2024-08-12 09:00' },
                                                                                { day: 'Day 3', status: 'Completed', date: '2024-08-14 11:00' },
                                                                                { day: 'Day 7', status: 'Pending', date: 'Pending' }
                                                                            ].map((step, i) => (
                                                                                <div key={i} className="flex items-center gap-4 relative z-10">
                                                                                    <div className={`w-6 h-6 rounded-full border-4 border-white flex items-center justify-center ${step.status === 'Completed' ? 'bg-emerald-500' : 'bg-slate-300'}`}>
                                                                                        <span className="material-symbols-outlined text-[10px] text-white">
                                                                                            {step.status === 'Completed' ? 'check' : 'schedule'}
                                                                                        </span>
                                                                                    </div>
                                                                                    <div>
                                                                                        <p className="text-[10px] font-black text-slate-700 leading-none">{step.day}</p>
                                                                                        <p className="text-[9px] font-bold text-slate-400 mt-0.5">{step.date}</p>
                                                                                    </div>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div className="space-y-6">
                                                                    <div>
                                                                        <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Incubation Details</h4>
                                                                        <div className="p-3 bg-white border border-slate-100 rounded-xl space-y-2">
                                                                            <div className="flex justify-between">
                                                                                <span className="text-[9px] font-bold text-slate-400">Start Date</span>
                                                                                <span className="text-[10px] font-black text-slate-700">{test.startDate}</span>
                                                                            </div>
                                                                            <div className="flex justify-between">
                                                                                <span className="text-[9px] font-bold text-slate-400">Expected Completion</span>
                                                                                <span className="text-[10px] font-black text-slate-700">{test.expectedCompletion}</span>
                                                                            </div>
                                                                            <div className="flex justify-between">
                                                                                <span className="text-[9px] font-bold text-slate-400">Avg Temperature</span>
                                                                                <span className="text-[10px] font-black text-emerald-600">32.4°C</span>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <div>
                                                                        <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Tube IDs</h4>
                                                                        <div className="flex flex-wrap gap-1.5">
                                                                            {['TB-001', 'TB-002', 'TB-003', 'TB-004'].map(id => (
                                                                                <span key={id} className="px-2 py-1 bg-white border border-slate-200 rounded text-[9px] font-mono font-black text-slate-600">
                                                                                    {id}
                                                                                </span>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div className="col-span-2 space-y-4">
                                                                    <div>
                                                                        <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Audit Trail & QA Remarks</h4>
                                                                        <div className="p-4 bg-white border border-slate-100 rounded-xl space-y-4 min-h-[140px]">
                                                                            <div className="flex gap-3">
                                                                                <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5"></div>
                                                                                <div>
                                                                                    <p className="text-[10px] font-black text-slate-700">Test initiated by system automation</p>
                                                                                    <p className="text-[9px] font-bold text-slate-400 mt-0.5">Aug 12, 2024 • 08:30 AM</p>
                                                                                </div>
                                                                            </div>
                                                                            <div className="p-3 bg-slate-50 rounded-lg border-l-4 border-blue-500">
                                                                                <p className="text-[10px] font-bold text-slate-600 leading-relaxed italic">
                                                                                    "Sample integrity verified upon arrival at incubation station. Membrane filtration performed under ISO Class 5 conditions."
                                                                                </p>
                                                                                <div className="flex items-center gap-2 mt-2">
                                                                                    <div className="w-4 h-4 rounded-full bg-slate-200 text-[8px] flex items-center justify-center font-black">SC</div>
                                                                                    <span className="text-[9px] font-black text-slate-400">Analyst Remarks</span>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                    </motion.tr>
                                                )}
                                            </AnimatePresence> */}
                                        </React.Fragment>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={10} className="px-4 py-20">
                                            <div className="flex flex-col items-center justify-center text-center space-y-4">
                                                <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center text-slate-300">
                                                    <span className="material-symbols-outlined text-5xl">biotech</span>
                                                </div>
                                                <div>
                                                    <h3 className="text-lg font-black text-slate-700">No Sterility Tests Found</h3>
                                                    <p className="text-sm font-medium text-slate-400 mt-1">Try adjusting your search or filters to find what you're looking for.</p>
                                                </div>
                                                <button
                                                    onClick={() => setIsInitiating(true)}
                                                    className="px-6 py-2 bg-blue-600 text-white rounded-xl text-[11px] font-black uppercase tracking-widest hover:bg-blue-700 transition-all shadow-lg shadow-blue-500/20"
                                                >
                                                    Create Sterility Test
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                </div>

                <Pagination
                    page={page}
                    totalPages={totalPages}
                    handlePageChange={handlePageChange}
                />

            </div>
            )}
        </div>
    );
};

export default SterilityTesting;
